import {
  TextField,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  RadioGroup,
  Radio,
} from '@mui/material'
import React from 'react'
import { useTheme } from '@mui/material/styles'
import { Controller, Control } from 'react-hook-form'
import { Checkbox as CustomCheckbox } from 'assets/icons/Checkbox'
import { RadioButton } from 'assets/icons/RadioButton'
import { QuestionType, Question } from 'types/types'
import { FormData } from '../../../components/forms/DynamicForm'

type QuestionHandler<T extends Question> = (
  question: T,
  control: Control<FormData, any>,
) => React.ReactNode

type QuestionHandlers = {
  [K in keyof typeof QuestionType]: QuestionHandler<Extract<Question, { type: K }>>
}

const questionMapper: QuestionHandlers = {
  [QuestionType.TEXT]: (question, control) => {
    const theme = useTheme<any>()

    const primaryColor =
      theme?.components?.MuiButton?.styleOverrides?.containedPrimary?.backgroundColor

    return (
      <Box>
        <Typography
          sx={{ fontSize: '16px', color: 'rgba(53, 53, 53, 1)', mb: 2, textAlign: 'center' }}
        >
          {question.title}
        </Typography>
        <Controller
          name={`${question.id}_${question.workflow_id}`}
          rules={{ required: true }}
          control={control}
          render={({ field }) => (
            <TextField
              InputProps={{
                style: {
                  borderRadius: '8px',
                  border: '1px solid #DDE6F1',
                },
              }}
              sx={{
                borderRadius: '8px',
                backgroundColor: 'white',

                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: primaryColor,
                },
                '& .MuiOutlinedInput-root.Mui-focused  .MuiOutlinedInput-notchedOutline': {
                  borderColor: primaryColor,
                },
              }}
              fullWidth
              type="text"
              {...field}
            />
          )}
        />
      </Box>
    )
  },
  [QuestionType.NUMBER]: (question, control) => {
    const theme = useTheme<any>()

    const primaryColor =
      theme?.components?.MuiButton?.styleOverrides?.containedPrimary?.backgroundColor

    return (
      <Box>
        <Typography
          sx={{ fontSize: '16px', color: 'rgba(53, 53, 53, 1)', mb: 2, textAlign: 'center' }}
        >
          {question.title}
        </Typography>
        <Controller
          name={`${question.id}_${question.workflow_id}`}
          rules={{ required: true }}
          control={control}
          render={({ field }) => (
            <TextField
              InputProps={{
                style: {
                  borderRadius: '8px',
                },
              }}
              sx={{
                borderRadius: '8px',
                backgroundColor: 'white',
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: primaryColor,
                },
                '& .MuiOutlinedInput-root.Mui-focused  .MuiOutlinedInput-notchedOutline': {
                  borderColor: primaryColor,
                },
              }}
              fullWidth
              type="number"
              {...field}
            />
          )}
        />
      </Box>
    )
  },
  [QuestionType.CHECKBOX]: (question, control) => {
    const theme = useTheme<any>()

    const primaryColor =
      theme?.components?.MuiButton?.styleOverrides?.containedPrimary?.backgroundColor

    return (
      <Box display="flex" flexDirection="column">
        <Typography
          textAlign="center"
          sx={{
            color: 'rgba(53, 53, 53, 1)',
            mb: 2,
          }}
        >
          {question.title}
        </Typography>
        <Box ml={1} display="flex" flexDirection="column" sx={{ width: '100%' }}>
          {question.options.map((option, index) => (
            <FormControlLabel
              key={index}
              sx={{
                mb: 1,
                backgroundColor: 'white',
                width: '100%',
                background: control._getWatch(
                  `${question.id}_${option.id}_${option.description}_${question.workflow_id}`,
                )
                  ? `${primaryColor}10`
                  : 'white',
                boxShadow: control._getWatch(
                  `${question.id}_${option.id}_${option.description}_${question.workflow_id}`,
                )
                  ? `1px 2px 10px 0px ${primaryColor}30 inset`
                  : '1px 3px 16px 0px rgba(0, 0, 0, 0.1)',
                borderRadius: '12px',
                height: '48px',
                border: control._getWatch(
                  `${question.id}_${option.id}_${option.description}_${question.workflow_id}`,
                )
                  ? `2px solid ${primaryColor} `
                  : '',
              }}
              control={
                <Controller
                  control={control}
                  name={`${question.id}_${option.id}_${option.description}_${question.workflow_id}`}
                  render={({ field }) => (
                    <Checkbox
                      icon={<CustomCheckbox.CustomCheckbox />}
                      checkedIcon={<CustomCheckbox.CheckedCustomCheckbox color={primaryColor} />}
                      checked={!!field.value ?? false}
                      {...field}
                      sx={{
                        borderRadius: '12px',
                        '&.Mui-checked': {
                          '&, & + .MuiFormControlLabel-label': {
                            color: primaryColor,
                          },
                        },
                      }}
                    />
                  )}
                />
              }
              label={option.description}
            />
          ))}
        </Box>
      </Box>
    )
  },
  [QuestionType.SELECT]: (question, control) => {
    const theme = useTheme<any>()

    const primaryColor =
      theme?.components?.MuiButton?.styleOverrides?.containedPrimary?.backgroundColor
    return (
      <Box>
        <Typography
          sx={{ fontSize: '16px', textAlign: 'center', mb: 2, color: 'rgba(53, 53, 53, 1)' }}
        >
          {question.title}
        </Typography>
        <FormControl fullWidth>
          <Controller
            control={control}
            rules={{ required: true }}
            name={`${question.id}_${question.workflow_id}`}
            render={({ field }) => (
              <Select
                sx={{
                  textAlign: 'left',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: primaryColor,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: primaryColor,
                  },
                }}
                variant="outlined"
                {...field}
                SelectDisplayProps={{
                  style: {
                    borderRadius: '8px',
                  },
                }}
              >
                {question.options.map((option) => (
                  <MenuItem key={option.id} value={`${option.id}_${option.description}`}>
                    {option.description}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </FormControl>
      </Box>
    )
  },
  [QuestionType.RADIO_BUTTON]: (question, control) => {
    const theme = useTheme<any>()
    const primaryColor =
      theme?.components?.MuiButton?.styleOverrides?.containedPrimary?.backgroundColor

    return (
      <Box display="flex" flexDirection="column" sx={{ width: '100%' }}>
        <Typography
          textAlign="center"
          sx={{
            mb: 2,
            fontSize: '16px',
            color: 'rgba(53, 53, 53, 1)',
          }}
        >
          {question.title}
        </Typography>
        <Controller
          rules={{ required: true }}
          name={`${question.id}_${question.workflow_id}`}
          render={({ field, formState, fieldState }) => {
            return (
              <RadioGroup
                {...field}
                sx={{
                  width: '100%',
                  ml: 1,
                }}
              >
                {question.options.map((option) => (
                  <FormControlLabel
                    sx={{
                      background:
                        control._getWatch(`${question.id}_${question.workflow_id}`) ===
                        `${option.id}_${option.description}`
                          ? `${primaryColor}10`
                          : 'white',
                      mb: 1,
                      width: '100%',
                      boxShadow:
                        control._getWatch(`${question.id}_${question.workflow_id}`) ===
                        `${option.id}_${option.description}`
                          ? `1px 2px 10px 0px ${primaryColor}30 inset`
                          : '1px 3px 16px 0px rgba(0, 0, 0, 0.1)',
                      borderRadius: '12px',
                      height: '48px',
                      border:
                        control._getWatch(`${question.id}_${question.workflow_id}`) ===
                        `${option.id}_${option.description}`
                          ? `2px solid ${primaryColor} `
                          : '',
                    }}
                    key={option.id}
                    control={
                      <Radio
                        sx={{
                          '&.Mui-checked': {
                            '&, & + .MuiFormControlLabel-label': {
                              color: primaryColor,
                            },
                          },
                        }}
                        icon={<RadioButton.CustomRadioButton />}
                        checkedIcon={<RadioButton.CustomRadioButtonChecked color={primaryColor} />}
                      />
                    }
                    value={`${option.id}_${option.description}`}
                    label={option.description}
                  />
                ))}
              </RadioGroup>
            )
          }}
          control={control}
        />
      </Box>
    )
  },
}

export const parseQuestionType = (question: Question, control: Control<FormData, any>) => {
  const parseFunction = questionMapper[question.type] as QuestionHandler<Question>
  return parseFunction ? parseFunction(question, control) : null
}
